<script>
  import { onDestroy } from "svelte";
  import alertify from "alertifyjs";
  import { ICONS } from "../js/icons";
  import backend from "../server";
  import CampaignFilters from "../lib/components/modals/freebets/altenar/CampaignFilters.svelte";
  import SportBonusCampaignTable from "../lib/components/modals/freebets/altenar/SportBonusCampaignTable.svelte";
  import SportBonusPlayersModal from "../lib/components/modals/freebets/altenar/SportBonusPlayersModal.svelte";
  import SportBonusCampaignConfigModal from "../lib/components/modals/freebets/altenar/SportBonusCampaignConfigModal.svelte";
  import { CampaignAPI } from "../lib/utils/freebetsApi.js";
  import { loadAnakatechClients } from "../lib/components/modals/freebets/anakatech/utils/anakatechCatalogs.js";
  import { CAMPAIGN_STATUS } from "../lib/utils/statusEngine.js";

  export let icons = ICONS;

  const providerId = 67;
  const providerCode = "ank";
  let filters = {
    client: "",
    clientId: "",
    clientName: "",
    campaignName: "",
    type: "",
    statuses: [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.SCHEDULED],
  };
  let sportBonusListRef;
  let isSyncing = false;
  let playerTarget = null;
  let playerTargetClient = null;
  let showSportBonusPlayerForm = false;
  let isUpdatingRecord = false;
  let assignmentProgress = null;
  let assignmentProgressTimer = null;
  let sportConfigTarget = null;
  let isSavingSportConfig = false;

  const api = {
    listCampaigns: CampaignAPI.listByProviderIgp,
    syncSportBonus: backend.freespin.syncSportBonusAnakatech,
    createSportBonusBatch: backend.freespin.createPlayersOpenBonusBatchAnakatech,
    updateSportBonusConfig: backend.freespin.updateSportBonusConfigAnakatech,
    cancelSportBonus: backend.freespin.cancelPlayerActiveBonus,
    deleteSportCampaign: backend.freespin.deactivateSportCampaignAnakatech,
  };

  function clearAssignmentProgress() {
    if (assignmentProgressTimer) clearTimeout(assignmentProgressTimer);
    assignmentProgressTimer = null;
    assignmentProgress = null;
  }

  function showFinalAssignmentProgress(progress) {
    clearAssignmentProgress();
    assignmentProgress = progress;
    assignmentProgressTimer = setTimeout(() => {
      assignmentProgress = null;
      assignmentProgressTimer = null;
    }, 5000);
  }

  onDestroy(clearAssignmentProgress);

  const getSportBonusTypes = (type) =>
    type ? [type] : ["FREE_BET", "CASHBACK", "RISK_FREE_BET"];

  function recordClientCode(record) {
    const code =
      record?.clientCode ??
      record?.client?.clientCode ??
      record?.client?.code ??
      (["string", "number"].includes(typeof record?.client) ? record.client : "");
    return code === null || code === undefined ? "" : String(code).trim();
  }

  async function synchronizeSportBonus() {
    if (!filters.client) {
      alertify.warning("Selecciona un cliente para realizar la sincronización");
      return;
    }
    isSyncing = true;
    try {
      const { clients } = await loadAnakatechClients();
      const selectedClient = clients.find(
        (client) => String(client?.code) === String(filters.client),
      );
      const currency = String(selectedClient?.currency ?? "").trim().toUpperCase();
      if (!currency) throw new Error("El cliente no tiene una moneda configurada");
      const rawClientId = String(filters.clientId ?? "").trim();
      const numericClientId = Number(rawClientId);
      const client = {
        clientId: rawClientId && Number.isFinite(numericClientId) ? numericClientId : null,
        clientCode: filters.client,
        clientName: filters.clientName,
      };
      await Promise.all(
        getSportBonusTypes(filters.type).map((sportBonusType) =>
          api.syncSportBonus({ providerId, sportBonusType, currency, client }),
        ),
      );
      await sportBonusListRef?.refresh?.();
      alertify.success("Campañas Sport Bonus sincronizadas correctamente");
    } catch (error) {
      console.error("Error sincronizando Sport Bonus de Altenar:", error);
      alertify.error("No se pudo completar la sincronización");
    } finally {
      isSyncing = false;
    }
  }

  async function openPlayerManagement(record) {
    const campaignClientCode = recordClientCode(record);
    let resolvedClient = null;
    try {
      const { clients } = await loadAnakatechClients();
      resolvedClient = clients.find(
        (client) => String(client?.code) === campaignClientCode,
      );
    } catch (error) {
      console.error("Error resolviendo el cliente de la campaña:", error);
    }
    const clientId =
      record?.client?.id ?? record?.client?.clientId ?? resolvedClient?.id ?? "";
    if (!clientId) {
      alertify.error("No se pudo resolver el cliente de la campaña");
      return;
    }
    playerTarget = record;
    playerTargetClient = resolvedClient || {
      id: clientId,
      code: campaignClientCode,
      currency: record?.currency,
    };
    clearAssignmentProgress();
    showSportBonusPlayerForm = true;
  }

  function targetClientId() {
    return playerTarget?.client?.id ??
      playerTarget?.client?.clientId ??
      playerTargetClient?.id ??
      "";
  }

  function assignedPlayerIds() {
    return (playerTarget?.playerList || [])
      .map((player) => player?.playerId ?? player?.id)
      .filter((id) => id !== null && id !== undefined)
      .map(String);
  }

  async function refreshOpenCampaign() {
    if (!playerTarget?.campaignId) return;
    try {
      const resolvedClientId = playerTarget?._resolvedClientId;
      const campaign = await CampaignAPI.getCampaignByCampaignIdIgp(playerTarget.campaignId);
      if (campaign) {
        playerTarget = { ...playerTarget, ...campaign, _resolvedClientId: resolvedClientId };
      }
    } catch (error) {
      console.warn("No se pudo refrescar la campaña abierta de Sport Bonus:", error);
    }
  }

  async function createSportBonusForPlayers(event) {
    const assignments = event.detail?.assignments || [];
    if (isUpdatingRecord || !playerTarget || !assignments.length) return;
    isUpdatingRecord = true;
    clearAssignmentProgress();
    assignmentProgress = { done: 0, total: assignments.length, errors: 0 };
    try {
      const response = await api.createSportBonusBatch(
        recordClientCode(playerTarget),
        { players: assignments.map((assignment) => assignment.payload) },
      );
      const result = response?.data ?? response ?? {};
      const total = Number(result.total ?? assignments.length);
      const successful = Number(result.successful ?? 0);
      const errors = Number(result.failed ?? Math.max(0, total - successful));
      showFinalAssignmentProgress({ done: total, total, errors });
      await sportBonusListRef?.refresh?.();
      await refreshOpenCampaign();
      if (errors) {
        alertify.warning(`${total - errors} asignaciones completadas y ${errors} con error`);
      } else {
        alertify.success(`${total} Sport Bonus asignados correctamente`);
      }
    } catch (error) {
      console.error("Error asignando Sport Bonus en Altenar:", error);
      showFinalAssignmentProgress({
        done: assignments.length,
        total: assignments.length,
        errors: assignments.length,
      });
      alertify.error("No se pudieron asignar los Sport Bonus");
    } finally {
      isUpdatingRecord = false;
    }
  }

  async function saveSportBonusConfig(event) {
    if (isSavingSportConfig || !sportConfigTarget?.campaignId) return;
    isSavingSportConfig = true;
    try {
      await api.updateSportBonusConfig(sportConfigTarget.campaignId, event.detail);
      await sportBonusListRef?.refresh?.();
      sportConfigTarget = null;
      alertify.success("Configuración de Sport Bonus guardada correctamente");
    } catch (error) {
      console.error("Error guardando configuración de Sport Bonus:", error);
      alertify.error(error?.response?.data?.message || "No se pudo guardar la configuración");
    } finally {
      isSavingSportConfig = false;
    }
  }

  async function cancelSportBonusForPlayer(event) {
    if (isUpdatingRecord || !playerTarget) return;
    isUpdatingRecord = true;
    try {
      await api.cancelSportBonus({
        ...event.detail,
        clientCode: recordClientCode(playerTarget),
      });
      await sportBonusListRef?.refresh?.();
      await refreshOpenCampaign();
      alertify.success(
        event.detail?.allBonuses
          ? "Todos los Sport Bonus del jugador fueron cancelados"
          : "Sport Bonus cancelado correctamente",
      );
    } catch (error) {
      console.error("Error cancelando Sport Bonus en Altenar:", error);
      alertify.error("No se pudo cancelar el Sport Bonus");
    } finally {
      isUpdatingRecord = false;
    }
  }

  async function deleteSportCampaign(campaign) {
    const clientCode = recordClientCode(campaign);
    const campaignId = String(campaign?.campaignId ?? campaign?.id ?? "").trim();
    if (!clientCode || !campaignId) {
      alertify.error("La campaña no tiene cliente o identificador válido");
      return;
    }
    if (!confirm(`¿Eliminar manualmente la campaña ${campaign.campaignName || campaignId}?`)) return;
    try {
      await api.deleteSportCampaign(clientCode, campaignId);
      await sportBonusListRef?.refresh?.();
      alertify.success("Campaña eliminada correctamente");
    } catch (error) {
      console.error("Error eliminando campaña Sport Bonus:", error);
      alertify.error("No se pudo eliminar la campaña");
    }
  }
</script>

<CampaignFilters bind:filters syncing={isSyncing} on:sync={synchronizeSportBonus} />

<SportBonusCampaignTable
  bind:this={sportBonusListRef}
  {api}
  provider={providerCode}
  {filters}
  {icons}
  on:addplayers={(event) => openPlayerManagement(event.detail)}
  on:editconfig={(event) => (sportConfigTarget = event.detail)}
  on:delete={(event) => deleteSportCampaign(event.detail)}
/>

{#if showSportBonusPlayerForm && playerTarget}
  <SportBonusPlayersModal
    campaign={playerTarget}
    clientId={targetClientId()}
    {providerId}
    excludeIds={assignedPlayerIds()}
    busy={isUpdatingRecord}
    progress={assignmentProgress}
    on:submit={createSportBonusForPlayers}
    on:cancelbonus={cancelSportBonusForPlayer}
    on:close={() => {
      showSportBonusPlayerForm = false;
      clearAssignmentProgress();
    }}
  />
{/if}

{#if sportConfigTarget}
  <SportBonusCampaignConfigModal
    campaign={sportConfigTarget}
    busy={isSavingSportConfig}
    on:save={saveSportBonusConfig}
    on:close={() => (sportConfigTarget = null)}
  />
{/if}

<style>
  :global(.btn-pink) { background-color: #2a1a28; color: white; }
  :global(.btn-pink:hover) { background-color: #ff0090; color: white; }
</style>
